import React, { useState } from 'react';

const CA_CITIES = [
  { name: 'Los Angeles',  x: 287.1, y: 580.9, customers: 75,  note: '#1 CA solar market · SCE territory' },
  { name: 'San Diego',    x: 336.2, y: 676.9, customers: 52,  note: 'High SDG&E rates drive strong ROI' },
  { name: 'San Jose',     x: 121.0, y: 343.5, customers: 44,  note: 'High income + PG&E TOU rates' },
  { name: 'Sacramento',   x: 139.2, y: 254.0, customers: 35,  note: 'SMUD + PG&E, strong SGIP uptake' },
  { name: 'Fresno',       x: 216.6, y: 386.8, customers: 28,  note: '300+ sun days/yr · High PG&E bills' },
  { name: 'Bakersfield',  x: 251.6, y: 485.7, customers: 24,  note: 'Central Valley heat drives savings' },
  { name: 'Riverside',    x: 325.3, y: 588.1, customers: 20,  note: 'Inland Empire · Strong SCE market' },
  { name: 'Lancaster',    x: 292.1, y: 534.0, customers: 16,  note: 'DAC + Fire zone SGIP stacking' },
  { name: 'Saratoga',     x: 115.1, y: 349.3, customers: 14,  note: 'Silicon Valley · High-value systems' },
  { name: 'Stockton',     x: 148.3, y: 298.0, customers: 12,  note: 'PG&E DAC zone — top SGIP rebates' },
];

const MAX = 75;
const CA_PATH = "M 15.5,7.2 L 70.5,7.2 L 116.0,7.2 L 161.5,7.2 L 207.0,7.2 L 252.5,7.2 L 298.0,7.2 L 342.6,7.2 L 451.3,512.4 L 451.3,521.8 L 454.1,534.7 L 458.6,550.6 L 462.7,576.6 L 469.1,583.8 L 473.6,606.2 L 456.3,617.0 L 447.2,627.8 L 447.2,654.5 L 447.2,676.9 L 338.0,690.6 L 332.6,680.5 L 326.2,642.3 L 279.8,603.3 L 274.4,583.1 L 247.5,577.3 L 211.6,578.8 L 177.9,543.4 L 177.9,503.0 L 151.5,464.0 L 126.0,417.1 L 120.6,396.9 L 122.4,373.1 L 108.3,356.5 L 97.8,342.1 L 92.8,311.8 L 92.8,300.9 L 76.0,276.4 L 64.6,259.1 L 38.7,223.7 L 34.1,198.5 L 20.0,168.9 L 8.6,132.8 L 6.4,119.8 L 6.4,93.8 L 15.5,79.4 L 21.4,52.0 L 15.5,27.4 L 15.5,7.2 Z";

// variant: 'gold' (residential) | 'navy' (commercial)
export default function CAMap({ variant = 'navy' }) {
  const [hovered, setHovered] = useState(null);

  const isGold = variant === 'gold';
  const dotColor      = isGold ? '#F5A623' : '#1A2B5F';
  const dotHoverColor = isGold ? '#1A2B5F' : '#F5A623';
  const mapFill       = isGold ? '#FFF8EC' : '#EEF2FF';
  const mapStroke     = isGold ? '#F5A623' : '#818CF8';
  const tooltipBg     = isGold ? '#1A2B5F' : '#1A2B5F';

  return (
    <div style={{
      background: '#fff', borderRadius: 20,
      boxShadow: '0 8px 40px rgba(26,43,95,0.1)',
      padding: '2rem', border: '1px solid var(--gray-100)',
      display: 'flex', flexDirection: 'column',
      height: '100%', boxSizing: 'border-box',
    }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: '0.3rem' }}>
          Customer Map
        </span>
        <h3 style={{ fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '1.05rem', color: 'var(--navy)', margin: 0, lineHeight: 1.3 }}>
          Solar Customers Across California
        </h3>
        <p style={{ fontSize: '0.73rem', color: 'var(--gray-400)', marginTop: '0.3rem', lineHeight: 1.5 }}>
          Based on LBNL Tracking the Sun + CPUC SGIP data · Hover a city
        </p>
      </div>

      <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <svg viewBox="0 0 480 700" style={{ width: '100%', maxHeight: 480, overflow: 'visible' }}>
          <defs>
            <radialGradient id={`mapGlow-${variant}`} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={isGold ? '#F5A623' : '#C7D2FE'} stopOpacity="0.2" />
              <stop offset="100%" stopColor={mapFill} stopOpacity="0" />
            </radialGradient>
          </defs>
          <path d={CA_PATH} fill={`url(#mapGlow-${variant})`} stroke="none" />
          <path d={CA_PATH} fill={mapFill} stroke={mapStroke} strokeWidth="2" strokeLinejoin="round" />

          {CA_CITIES.map(city => {
            const baseR = 8 + (city.customers / MAX) * 20;
            const isH = hovered?.name === city.name;
            const r = isH ? baseR * 1.5 : baseR;

            return (
              <g key={city.name}
                onMouseEnter={() => setHovered(city)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor: 'pointer' }}>
                {isH && (
                  <>
                    <circle cx={city.x} cy={city.y} r={r + 9} fill="none" stroke={dotHoverColor} strokeWidth="1.5" opacity="0.2" />
                    <circle cx={city.x} cy={city.y} r={r + 5} fill="none" stroke={dotHoverColor} strokeWidth="2" opacity="0.4" />
                  </>
                )}
                <circle cx={city.x + 1} cy={city.y + 2} r={r} fill="rgba(26,43,95,0.15)" />
                <circle
                  cx={city.x} cy={city.y} r={r}
                  fill={isH ? dotHoverColor : dotColor}
                  stroke="#fff" strokeWidth={isH ? 2.5 : 2}
                  style={{ transition: 'r 0.18s ease, fill 0.18s ease' }}
                />
                <circle cx={city.x - r * 0.28} cy={city.y - r * 0.28} r={r * 0.25}
                  fill="rgba(255,255,255,0.3)" />
              </g>
            );
          })}
        </svg>

        {hovered && (
          <div style={{
            position: 'absolute', top: 8, right: 8,
            background: tooltipBg, color: '#fff',
            borderRadius: 14, padding: '0.9rem 1.1rem',
            minWidth: 170, maxWidth: 205,
            boxShadow: '0 8px 30px rgba(26,43,95,0.35)',
            pointerEvents: 'none', zIndex: 10,
            animation: 'fadeIn 0.15s ease',
          }}>
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              {hovered.name}
            </div>
            <div style={{ fontSize: '0.82rem', marginBottom: '0.3rem' }}>
              <span style={{ color: 'var(--gold)', fontWeight: 700, fontSize: '1rem' }}>{hovered.customers}</span>
              <span style={{ color: 'rgba(255,255,255,0.6)' }}> customers</span>
            </div>
            <div style={{ fontSize: '0.69rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.55 }}>
              {hovered.note}
            </div>
          </div>
        )}
      </div>

      <div style={{ paddingTop: '1rem', marginTop: '0.5rem', borderTop: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)' }}>Bubble size = # of customers</div>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--navy)' }}>
          {CA_CITIES.reduce((s, c) => s + c.customers, 0)}+ total customers
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
